import { IconName } from '@infotech-soe/erp-ui-kit';
import {
  ActionProcessMode,
  IContextMenuItemRefreshRules,
  THotKey,
} from '@infotech-soe/metaschema';

import { Caption } from '../../../models/caption/Caption';
import { ClientContext } from '../../context/IContextService';
import { CommandName } from '../CommandViewModel';
import CategoryCommandImpl from '../impl/CategoryCommandImpl';
import { UserInteractor, UserInteractorType } from '../UserInteractor';

export const enum RefreshStrategy {
  NONE = 'none',
  CURRENT = 'current',
  REMOVE = 'remove',
  ALL = 'all',
  NEW = 'new',
  DYNAMIC = 'dynamic',
}

abstract class CategoryCommand<T, RefreshBuilderProps = any> {
  constructor(protected commandImpl: CategoryCommandImpl<T>) {}

  protected refreshStrategyBuilder?: (
    payload: RefreshBuilderProps
  ) => RefreshStrategy;

  public execute(
    context: ClientContext,
    args: T
  ): Promise<void | Record<string, any>> {
    return this.commandImpl.execute(context, args);
  }

  public getInitialArgs(
    context: ClientContext,
    currentRecord?: Record<string, any>
  ): Promise<T> {
    return this.commandImpl.getInitialArgs(context, currentRecord);
  }

  public async getUserInteractor(): Promise<UserInteractor> {
    const baseInteractor = this.commandImpl.getUserInteractor();
    if (
      baseInteractor.type === UserInteractorType.DEFAULT_FORM ||
      baseInteractor.type === UserInteractorType.CONDITION_FORM ||
      baseInteractor.type === UserInteractorType.PRINT_FORM
    ) {
      return {
        ...baseInteractor,
        caption: await this.getCaption(),
      };
    }
    return baseInteractor;
  }

  public isAvailable(context: ClientContext): boolean {
    return this.commandImpl.isAvailable(context);
  }

  public getHotKey(): THotKey | undefined {
    return this.commandImpl.getHotKey();
  }

  public getName(): CommandName {
    return this.commandImpl.getName();
  }

  public getRefreshStrategy(): RefreshStrategy {
    return this.commandImpl.getRefreshStrategy();
  }

  public getRefreshStrategyBuilder():
    ((payload: RefreshBuilderProps) => RefreshStrategy) | undefined {
    if (this.getRefreshStrategy() !== RefreshStrategy.DYNAMIC) {
      return undefined;
    }

    if (typeof this.refreshStrategyBuilder !== 'function') {
      throw new Error(
        `command[${this.getName()}] should have refreshStrategyBuilder`
      );
    }

    return this.refreshStrategyBuilder;
  }

  public getRefreshRules(): IContextMenuItemRefreshRules | undefined {
    return this.commandImpl.getRefreshRules();
  }

  public abstract getCaption(): Promise<Caption>;

  public abstract getIcon(): IconName;

  public getMenuLabel(): string | undefined {
    return this.commandImpl.getMenuLabel();
  }

  public getCommandImpl(): CategoryCommandImpl<T> {
    return this.commandImpl;
  }

  public getProcessMode(): ActionProcessMode {
    return this.commandImpl.getProcessMode();
  }

  public getBadge(context: ClientContext): string | number | undefined {
    return this.commandImpl.getBadge(context);
  }
}

export default CategoryCommand;
